import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BearerToken } from '@marageti/z4-sdk/lib/auth';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { TeamMember } from '@marageti/z4-sdk/lib/people';
import {
  hasAuthorizedRole,
  LocalStorageService,
  useQueryParams,
  useApiClient,
} from '@marageti/z4-lib';
import { GoogleLoginRequest, Token } from '@marageti/z4-sdk/lib/auth/Token';
import LoginView from './activate-view';
import { useAuthStore } from '../../store';
import { authorizedRoles, getMatchingUser } from '../../utils/auth';

const LocalStorage = new LocalStorageService();

const ActivateContainer = () => {
  const {
    isAuthenticated,
    removeAuthenticatedTeamMember,
    setIsAuthenticated,
    setAuthenticatedTeamMember,
  } = useAuthStore();
  const apiClient = useApiClient();
  const navigate = useNavigate();

  const { redirect } = useQueryParams();

  const [loginError, setLoginError] = useState<ApiError>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirect || '/');
    }
  }, [isAuthenticated]);

  const onActivationSuccess = (bearerToken: BearerToken): void => {
    const { accessToken } = bearerToken;
    apiClient.instance.setAuthToken(accessToken);

    apiClient.authClient.getToken(accessToken)
      .then((token: Token) => {
        const { email } = token;
        LocalStorage.addToLocalStorage('accessToken', accessToken);
        LocalStorage.addToLocalStorage('email', email);
        return apiClient.peopleClient.getTeamMember(email);
      })
      .then((data: TeamMember) => {
        const matchingUser = getMatchingUser(data.email, data.userInfo);
        if (!matchingUser || !hasAuthorizedRole(matchingUser.roles, authorizedRoles)) {
          throw new Error('Not Authorized');
        }
        setAuthenticatedTeamMember(data);
        setIsAuthenticated(true);
      })
      .catch((e: ApiError) => {
        removeAuthenticatedTeamMember();
        setIsAuthenticated(false);
        setLoginError(e);
      })
      .finally(() => setLoading(false));
  };

  // For Google SSO logging in will activate the user
  const onGoogleSSOSubmit = (loginRequest: GoogleLoginRequest): void => {
    setLoginError(undefined);
    setLoading(true);
    apiClient.authClient.login(loginRequest)
      .then(onActivationSuccess)
      .catch((e: ApiError) => {
        setIsAuthenticated(false);
        setLoginError(e);
        setLoading(false);
      });
  };

  return (
    <LoginView
      loading={loading}
      loginError={loginError}
      onGoogleSSOSubmit={onGoogleSSOSubmit}
    />
  );
};

export default ActivateContainer;
